/*!
 * Minimal in-repo feature-detection module — replaces the legacy Modernizr v2.8.0
 * build (PBI 173832, Feature 173829 "Reduce the initial JavaScript bundle").
 *
 * Exposes only the surface actually consumed across the apps:
 *   touch, video/audio codec support, csstransforms/csstransforms3d/csstransitions,
 *   input.placeholder, hasEvent(), testStyles(), addTest(), _domPrefixes
 * and preserves Modernizr's two observable side effects:
 *   (1) it writes feature classes onto <html> (the design CSS keys off
 *       .webp/.no-webp, .touch/.no-touch, .video, .audio), and
 *   (2) it sets the global `self.Modernizr`.
 *
 * csstransforms/csstransforms3d/csstransitions/input.placeholder are hardcoded
 * true: they are supported by every browser the es2017 build target permits
 * (the only engine that lacked them — IE — cannot run an es2017 bundle anyway),
 * so the old runtime test always returned true here. touch, video/audio codecs
 * and pointer-event support stay computed via the same native APIs Modernizr used.
 *
 * Authored in ES2015 (no ES2018+ syntax) so it is safe on the es2017 browser
 * target even if this package is ever excluded from bundler transpilation —
 * which is also why the previous build shipped as raw ES5.
 */

const docElement = document.documentElement;

// Vendor DOM prefixes — consumed by nd.scripts.refresh.js to build legacy
// prefixed event-name permutations. Matches Modernizr's domPrefixes.
const domPrefixes = ['webkit', 'moz', 'o', 'ms'];

function detectTouch() {
    return !!(
        'ontouchstart' in window ||
        (window.DocumentTouch && document instanceof window.DocumentTouch) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0)
    );
}

// Mirror Modernizr: normalize a canPlayType result, stripping the legacy "no".
function canPlay(elem, type) {
    try {
        return elem.canPlayType(type).replace(/^no$/, '');
    } catch (e) {
        return '';
    }
}

function detectVideo() {
    const elem = document.createElement('video');
    if (!elem.canPlayType) {
        return false;
    }
    return {
        ogg: canPlay(elem, 'video/ogg; codecs="theora"'),
        h264: canPlay(elem, 'video/mp4; codecs="avc1.42E01E"'),
        webm: canPlay(elem, 'video/webm; codecs="vp8, vorbis"'),
    };
}

function detectAudio() {
    const elem = document.createElement('audio');
    if (!elem.canPlayType) {
        return false;
    }
    return {
        ogg: canPlay(elem, 'audio/ogg; codecs="vorbis"'),
        mp3: canPlay(elem, 'audio/mpeg;'),
        wav: canPlay(elem, 'audio/wav; codecs="1"'),
        m4a: canPlay(elem, 'audio/x-m4a;') || canPlay(elem, 'audio/aac;'),
    };
}

// Support for a named DOM event (e.g. 'pointerdown'); element defaults to window.
function hasEvent(eventName, element) {
    const target = element || window;
    return 'on' + eventName in target;
}

// Inject a style rule + probe element, run the callback against it, then clean up.
// Used to measure scrollbar dimensions.
function testStyles(rule, callback) {
    const div = document.createElement('div');
    div.id = 'modernizr';

    const style = document.createElement('style');
    style.textContent = rule;
    div.appendChild(style);

    const body = document.body || docElement;
    body.appendChild(div);

    try {
        callback(div);
    } finally {
        if (div.parentNode) {
            div.parentNode.removeChild(div);
        }
    }
}

// Append `name` (supported) or `no-name` (unsupported) to <html>, as Modernizr does.
function setClass(name, supported) {
    docElement.className += ' ' + (supported ? '' : 'no-') + name;
}

const Modernizr = {
    _domPrefixes: domPrefixes,

    touch: detectTouch(),
    video: detectVideo(),
    audio: detectAudio(),
    input: { placeholder: true },

    // Universally supported across the es2017 browser target → constant true.
    csstransforms: true,
    csstransforms3d: true,
    csstransitions: true,

    hasEvent: hasEvent,
    testStyles: testStyles,

    // Define a custom feature test (used by the webp shim). Mirrors Modernizr 2.8.0:
    // lowercases the name, refuses to overwrite an existing test, writes the html
    // class, and stores the result on the Modernizr object.
    addTest: function addTest(feature, test) {
        if (typeof feature === 'object') {
            for (const key in feature) {
                if (Object.prototype.hasOwnProperty.call(feature, key)) {
                    addTest(key, feature[key]);
                }
            }
            return Modernizr;
        }

        feature = feature.toLowerCase();
        if (Modernizr[feature] !== undefined) {
            return Modernizr;
        }

        const value = typeof test === 'function' ? test() : test;
        setClass(feature, !!value);
        Modernizr[feature] = value;
        return Modernizr;
    },
};

// --- side effects (run at import, before app render) ----------------------

// Swap no-js → js, then write the feature classes the design CSS depends on.
docElement.className = docElement.className.replace(/(^|\s)no-js(\s|$)/, '$1$2') + ' js';

setClass('touch', !!Modernizr.touch);
setClass('video', !!Modernizr.video);
setClass('audio', !!Modernizr.audio);
setClass('csstransforms', Modernizr.csstransforms);
setClass('csstransforms3d', Modernizr.csstransforms3d);
setClass('csstransitions', Modernizr.csstransitions);

self.Modernizr = Modernizr;

export { Modernizr };
